import {
  FollowUpActionStatus,
  type Home,
  type IncidentSeverityValues
} from '@lmc/ts-common'
import { Checkbox, FormCard, FormItem, TextArea } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Form as AntdForm, type FormInstance, Radio, Select } from 'antd'
import { getNominative } from 'common/utils'
import useBodyMaps from 'hooks/useBodyMaps'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useResidentsList from 'hooks/useResidentsList'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useCarers } from 'screens/Organisation/screens/TeamList'
import ActionsInput from 'screens/Residents/screens/ResidentsGoalsScreen/components/ActionPlanInput/ActionPlanInput'
import { actionPlanValidator } from 'screens/Residents/screens/ResidentsGoalsScreen/components/GoalForm/GoalForm'
import styled from 'styled-components'
import { getSeverityOptions } from '../../utils/incidentSeverityUtils'
import UploadAttachmentsOnSaveInput from '../UploadAttachmentsOnSaveInput'

type IncidentCaseFormFieldsProps = {
  form: FormInstance
  severities: IncidentSeverityValues | undefined
  residentId: string
  isSafeguardingIncident?: boolean
  testId?: string
}

export const followUpActionsFieldName = ['followUpActions', 'actions']

export const IncidentCaseFormFields = ({
  form,
  severities,
  residentId,
  isSafeguardingIncident,
  testId = 'incident-case-form-fields'
}: IncidentCaseFormFieldsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const currentHome: Home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const preferredPeopleNominative: string =
    currentHome.preferredPeopleNominative || ''
  const currentHomeId: string = currentHome.id

  const { residents } = useResidentsList({})

  const { bodyMaps } = useBodyMaps({ residentId })

  const { carers } = useCarers()

  const flags = useFeatureFlags({
    flagNames: ['incident_case_action_assignment']
  })?.data
  const flagIncidentCaseActionAssignment =
    flags?.incident_case_action_assignment?.enabled

  const serviceUsersOptions = residents
    ?.filter(
      (resident) =>
        resident.name.first !== undefined &&
        resident.name.last !== undefined &&
        resident.id !== residentId
    )
    .map((carer) => ({
      label: `${carer.name.first} ${carer.name.last}`,
      value: carer.id
    }))
  const carerOptions = carers
    ?.filter(
      (carerPerson) =>
        carerPerson.name.first !== undefined &&
        carerPerson.name.last !== undefined &&
        carerPerson.active &&
        (carerPerson.home === currentHomeId ||
          carerPerson.homes.includes(currentHomeId))
    )
    .map((carer) => ({
      label: `${carer.name.first} ${carer.name.last}`,
      value: carer.id
    }))

  const bodyMapOptions = bodyMaps?.map((bodyMap) => ({
    value: bodyMap.id,
    label: bodyMap.title
  }))
  const physicalIntervention = AntdForm.useWatch(
    (values) => values.physicalIntervention?.hasBeenApplied,
    form
  )
  const restraints = AntdForm.useWatch(
    (values) => values.restraints?.hasBeenApplied,
    form
  )
  const chemicalRestraints = AntdForm.useWatch(
    (values) => values.chemicalRestraints?.hasBeenApplied,
    form
  )
  const environmentalRestraints = AntdForm.useWatch(
    (values) => values.environmentalRestraints?.hasBeenApplied,
    form
  )
  const verbalRestraints = AntdForm.useWatch(
    (values) => values.verbalRestraints?.hasBeenApplied,
    form
  )
  const residentInjuries = AntdForm.useWatch(
    (values) => values.residentInjuries?.hasBeenApplied,
    form
  )
  const carersInjuries = AntdForm.useWatch(
    (values) => values.carersInjuries?.hasBeenApplied,
    form
  )
  const otherPartiesInjuries = AntdForm.useWatch(
    (values) => values.otherPartiesInjuries?.hasBeenApplied,
    form
  )
  const damages = AntdForm.useWatch(
    (values) => values.damages?.hasBeenApplied,
    form
  )
  const damagesIntroducingRisk = AntdForm.useWatch(
    (values) => values.damagesIntroducingRisk?.hasBeenApplied,
    form
  )

  const restrictivePracticesStatus = AntdForm.useWatch(
    ['restrictivePracticesStatus'],
    form
  )
  const injuriesStatus = AntdForm.useWatch(['injuriesStatus'], form)
  const damagesStatus = AntdForm.useWatch(['damagesStatus'], form)

  const prnMedication = AntdForm.useWatch(
    (values) => values.prnMedication?.hasBeenApplied,
    form
  )

  const filterOption = (input: string, option?: { label: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const severityOptions = getSeverityOptions(severities, t)
  const actionInputOptions = Object.keys(FollowUpActionStatus).map(
    (status) => ({
      label: t(
        incidentReportsConstants[
          FollowUpActionStatus[status as FollowUpActionStatus]
        ]
      ),
      value: FollowUpActionStatus[status as FollowUpActionStatus]
    })
  )

  return (
    <Wrapper data-testid={testId}>
      <Title level={2} data-testid={`${testId}-title`}>
        {t(incidentReportsConstants.INCIDENT_CASE)}
      </Title>
      <FormCard
        testId={`${testId}-incident-details`}
        title={t(incidentReportsConstants.INCIDENT_DETAILS)}
      >
        <FormItem
          data-testid={`${testId}-severity-field`}
          label={<Text>{t(incidentReportsConstants.SEVERITY)}</Text>}
          name="severity"
        >
          <Select
            data-testid={`${testId}-severity-select`}
            options={severityOptions}
          />
        </FormItem>
        <FormItem
          data-testid={`${testId}-service-users-involved-field`}
          label={
            <Text>
              {t(incidentReportsConstants.OTHER_SERVICE_USERS_INVOLVED, {
                variable: getNominative(preferredPeopleNominative, 'residents')
              }) + ':'}
            </Text>
          }
          name={['serviceUsersInvolved']}
          colon={false}
        >
          <Select
            data-testid={`${testId}-service-users-involved-select`}
            mode="multiple"
            allowClear
            options={serviceUsersOptions}
            filterOption={filterOption}
          />
        </FormItem>
        <FormItem
          data-testid={`${testId}-other-people-involved-field`}
          label={
            <Text>{t(incidentReportsConstants.OTHER_PEOPLE_INVOLVED)}</Text>
          }
          name={['otherPeopleInvolved']}
        >
          <TextArea
            data-testid={`${testId}-other-people-involved-textarea`}
            autoSize={{
              minRows: 3
            }}
          />
        </FormItem>
        <FormItem
          data-testid={`${testId}-other-witnesses-field`}
          label={<Text>{t(incidentReportsConstants.OTHER_WITNESS)}</Text>}
          name={['otherWitnesses']}
        >
          <TextArea
            data-testid={`${testId}-other-witnesses-textarea`}
            autoSize={{
              minRows: 3
            }}
          />
        </FormItem>
        <FormItem
          data-testid={`${testId}-carers-involved-field`}
          label={<Text>{t(incidentReportsConstants.CARERES_INVOLVED)}</Text>}
          name={['carersInvolved']}
        >
          <Select
            data-testid={`${testId}-carers-involved-select`}
            mode="multiple"
            allowClear
            options={carerOptions}
          />
        </FormItem>
        <FormCard
          testId={`${testId}-restrictive-practices`}
          title={t(incidentReportsConstants.RESTRICTIVE_PRACTICES)}
          level={2}
        >
          <FormItem
            data-testid={`${testId}-restrictive-practices-status-field`}
            label={<Text>{'Were any restrictive practices used?'}</Text>}
            name={['restrictivePracticesStatus']}
          >
            <DamagesStatusRadioGroup>
              <Radio value="yes">Yes</Radio>
              <Radio value="no">No</Radio>
            </DamagesStatusRadioGroup>
          </FormItem>

          {restrictivePracticesStatus === 'yes' && (
            <>
              <FormItem
                data-testid={`${testId}-physical-intervention-field`}
                valuePropName="checked"
                name={['physicalIntervention', 'hasBeenApplied']}
              >
                <Checkbox
                  data-testid={`${testId}-physical-intervention-checkbox`}
                >
                  <Text>
                    {t(incidentReportsConstants.PHYSICAL_INTERVENTION_APPLIED)}
                  </Text>
                </Checkbox>
              </FormItem>
              {physicalIntervention && (
                <FormItem
                  data-testid={`${testId}-physical-intervention-details-field`}
                  name={['physicalIntervention', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-physical-intervention-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}

              <FormItem
                data-testid={`${testId}-restraints-field`}
                valuePropName="checked"
                name={['restraints', 'hasBeenApplied']}
              >
                <Checkbox data-testid={`${testId}-restraints-checkbox`}>
                  <Text>{t(incidentReportsConstants.RESTRAINTS_APPLIED)}</Text>
                </Checkbox>
              </FormItem>
              {restraints && (
                <FormItem
                  data-testid={`${testId}-restraints-details-field`}
                  name={['restraints', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-restraints-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}

              <FormItem
                data-testid={`${testId}-chemical-restraints-field`}
                valuePropName="checked"
                name={['chemicalRestraints', 'hasBeenApplied']}
              >
                <Checkbox
                  data-testid={`${testId}-chemical-restraints-checkbox`}
                >
                  <Text>
                    {t(incidentReportsConstants.CHEMICAL_RESTRAINTS_APPLIED)}
                  </Text>
                </Checkbox>
              </FormItem>
              {chemicalRestraints && (
                <FormItem
                  data-testid={`${testId}-chemical-restraints-details-field`}
                  name={['chemicalRestraints', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-chemical-restraints-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}

              <FormItem
                data-testid={`${testId}-environmental-restraints-field`}
                valuePropName="checked"
                name={['environmentalRestraints', 'hasBeenApplied']}
              >
                <Checkbox
                  data-testid={`${testId}-environmental-restraints-checkbox`}
                >
                  <Text>
                    {t(
                      incidentReportsConstants.ENVIRONMENTAL_RESTRAINTS_APPLIED
                    )}
                  </Text>
                </Checkbox>
              </FormItem>
              {environmentalRestraints && (
                <FormItem
                  data-testid={`${testId}-environmental-restraints-details-field`}
                  name={['environmentalRestraints', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-environmental-restraints-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}

              <FormItem
                data-testid={`${testId}-verbal-restraints-field`}
                valuePropName="checked"
                name={['verbalRestraints', 'hasBeenApplied']}
              >
                <Checkbox data-testid={`${testId}-verbal-restraints-checkbox`}>
                  <Text>
                    {t(incidentReportsConstants.VERBAL_RESTRAINTS_APPLIED)}
                  </Text>
                </Checkbox>
              </FormItem>
              {verbalRestraints && (
                <FormItem
                  data-testid={`${testId}-verbal-restraints-details-field`}
                  name={['verbalRestraints', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-verbal-restraints-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}
            </>
          )}

          {restrictivePracticesStatus === 'no' && (
            <FormItem
              data-testid={`${testId}-no-restraints-details-field`}
              label={
                <Text>
                  {t(incidentReportsConstants.NO_RESTRICTIVE_PRACTICES_DETAILS)}
                </Text>
              }
              name={['noRestraintsDetails']}
            >
              <TextArea
                data-testid={`${testId}-no-restraints-details-textarea`}
                autoSize={{ minRows: 3 }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard
          testId={`${testId}-injuries`}
          title={t(incidentReportsConstants.INJURIES)}
          level={2}
        >
          <FormItem
            data-testid={`${testId}-injuries-status-field`}
            label={<Text>{'Were there any injuries as a result?'}</Text>}
            name={['injuriesStatus']}
          >
            <DamagesStatusRadioGroup>
              <Radio value="yes">Yes</Radio>
              <Radio value="no">No</Radio>
            </DamagesStatusRadioGroup>
          </FormItem>

          {injuriesStatus === 'yes' && (
            <>
              <FormItem
                data-testid={`${testId}-resident-injuries-field`}
                valuePropName="checked"
                name={['residentInjuries', 'hasBeenApplied']}
              >
                <Checkbox data-testid={`${testId}-resident-injuries-checkbox`}>
                  <Text>
                    {t(incidentReportsConstants.INJURIES_OF_RESIDENT, {
                      variable: getNominative(preferredPeopleNominative)
                    })}
                  </Text>
                </Checkbox>
              </FormItem>
              {residentInjuries && (
                <FormItem
                  data-testid={`${testId}-resident-injuries-details-field`}
                  name={['residentInjuries', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-resident-injuries-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}
              {residentInjuries && (
                <FormItem
                  data-testid={`${testId}-resident-injuries-body-map-field`}
                  name={['residentInjuries', 'residentInjuriesBodyMap']}
                  label={
                    <Text>{t(incidentReportsConstants.RELATED_BODY)}</Text>
                  }
                  tooltip={t(incidentReportsConstants.YOU_CAN_SELECT)}
                >
                  <Select
                    data-testid={`${testId}-resident-injuries-body-map-select`}
                    optionRender={(option) => <Text>{option.label}</Text>}
                    options={bodyMapOptions}
                    allowClear
                  />
                </FormItem>
              )}

              <FormItem
                data-testid={`${testId}-carers-injuries-field`}
                valuePropName="checked"
                name={['carersInjuries', 'hasBeenApplied']}
              >
                <Checkbox data-testid={`${testId}-carers-injuries-checkbox`}>
                  <Text>{t(incidentReportsConstants.INJURIES_OF_CARERS)}</Text>
                </Checkbox>
              </FormItem>
              {carersInjuries && (
                <FormItem
                  data-testid={`${testId}-carers-injuries-details-field`}
                  name={['carersInjuries', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-carers-injuries-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}

              <FormItem
                data-testid={`${testId}-other-parties-injuries-field`}
                valuePropName="checked"
                name={['otherPartiesInjuries', 'hasBeenApplied']}
              >
                <Checkbox
                  data-testid={`${testId}-other-parties-injuries-checkbox`}
                >
                  <Text>
                    {t(incidentReportsConstants.INJURIES_OF_OTHER_PARTIES)}
                  </Text>
                </Checkbox>
              </FormItem>
              {otherPartiesInjuries && (
                <FormItem
                  data-testid={`${testId}-other-parties-injuries-details-field`}
                  name={['otherPartiesInjuries', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-other-parties-injuries-details-textarea`}
                    autoSize={{ minRows: 3 }}
                  />
                </FormItem>
              )}
            </>
          )}

          {injuriesStatus === 'no' && (
            <FormItem
              data-testid={`${testId}-no-injuries-details-field`}
              label={
                <Text>{t(incidentReportsConstants.NO_INJURIES_DETAILS)}</Text>
              }
              name={['noInjuriesDetails']}
            >
              <TextArea
                data-testid={`${testId}-no-injuries-details-textarea`}
                autoSize={{ minRows: 3 }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard
          testId={`${testId}-damages`}
          title={t(incidentReportsConstants.DAMAGES)}
          level={2}
        >
          <FormItem
            data-testid={`${testId}-damages-status-field`}
            label={<Text>{'Were there any damages as a result?'}</Text>}
            name={['damagesStatus']}
          >
            <DamagesStatusRadioGroup>
              <Radio value="yes">Yes</Radio>
              <Radio value="no">No</Radio>
            </DamagesStatusRadioGroup>
          </FormItem>

          {damagesStatus === 'yes' && (
            <>
              <FormItem
                data-testid={`${testId}-damages-field`}
                valuePropName="checked"
                name={['damages', 'hasBeenApplied']}
              >
                <Checkbox data-testid={`${testId}-damages-checkbox`}>
                  <Text>{t(incidentReportsConstants.PROPERTIES_OR_ENV)}</Text>
                </Checkbox>
              </FormItem>
              {damages && (
                <FormItem
                  data-testid={`${testId}-damages-details-field`}
                  name={['damages', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-damages-details-textarea`}
                    autoSize={{
                      minRows: 3
                    }}
                  />
                </FormItem>
              )}
              <FormItem
                data-testid={`${testId}-damages-introducing-risk-field`}
                valuePropName="checked"
                name={['damagesIntroducingRisk', 'hasBeenApplied']}
              >
                <Checkbox
                  data-testid={`${testId}-damages-introducing-risk-checkbox`}
                >
                  <Text>
                    {t(
                      incidentReportsConstants.DAMAGES_INTRODUCING_RISK_APPLIED
                    )}
                  </Text>
                </Checkbox>
              </FormItem>
              {damagesIntroducingRisk && (
                <FormItem
                  data-testid={`${testId}-damages-introducing-risk-details-field`}
                  name={['damagesIntroducingRisk', 'details']}
                  label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
                >
                  <TextArea
                    data-testid={`${testId}-damages-introducing-risk-details-textarea`}
                    autoSize={{
                      minRows: 3
                    }}
                  />
                </FormItem>
              )}
            </>
          )}

          {damagesStatus === 'no' && (
            <FormItem
              data-testid={`${testId}-no-damages-details-field`}
              label={
                <Text>{t(incidentReportsConstants.NO_DAMAGES_DETAILS)}</Text>
              }
              name={['noDamagesDetails']}
            >
              <TextArea
                data-testid={`${testId}-no-damages-details-textarea`}
                autoSize={{
                  minRows: 3
                }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard
          testId={`${testId}-medications`}
          title={t(incidentReportsConstants.MEDICATIONS)}
          level={2}
        >
          <FormItem
            data-testid={`${testId}-prn-medication-field`}
            valuePropName="checked"
            name={['prnMedication', 'hasBeenApplied']}
          >
            <Checkbox data-testid={`${testId}-prn-medication-checkbox`}>
              <Text>{t(incidentReportsConstants.PRN_MEDICATIONS)}</Text>
            </Checkbox>
          </FormItem>
          {prnMedication && (
            <FormItem
              data-testid={`${testId}-prn-medication-details-field`}
              name={['prnMedication', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                data-testid={`${testId}-prn-medication-details-textarea`}
                autoSize={{
                  minRows: 3
                }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard
          testId={`${testId}-service-user-opinion`}
          title={t(incidentReportsConstants.SU_OPINION, {
            variable: getNominative(preferredPeopleNominative)
          })}
          level={2}
        >
          <FormItem
            data-testid={`${testId}-service-user-opinion-field`}
            name={['serviceUserOpinion']}
          >
            <TextArea
              data-testid={`${testId}-service-user-opinion-textarea`}
              autoSize={{
                minRows: 3
              }}
            />
          </FormItem>
        </FormCard>
        <FormCard
          testId={`${testId}-additional-comment`}
          title={t(incidentReportsConstants.ADDITIONAL_COMMENT)}
          level={2}
        >
          <FormItem
            data-testid={`${testId}-additional-comment-field`}
            name={['incidentOtherInformation']}
          >
            <TextArea
              data-testid={`${testId}-additional-comment-textarea`}
              autoSize={{
                minRows: 3
              }}
            />
          </FormItem>
        </FormCard>
        <div data-testid={`${testId}-attachments-section`}>
          <FormItem
            data-testid={`${testId}-attachments-field`}
            label={<Text>{t(incidentReportsConstants.ATTACHMENTS)}</Text>}
            name={['attachments']}
          >
            <UploadAttachmentsOnSaveInput
              testId={`${testId}-attachments-upload`}
            />
          </FormItem>
        </div>
      </FormCard>
      <FormCard
        testId={`${testId}-follow-up-actions`}
        title={t(incidentReportsConstants.FOLLOW_UP_ACTIONS)}
      >
        {!isSafeguardingIncident && (
          <FormItem
            data-testid={`${testId}-safeguarding-field`}
            valuePropName="checked"
            name={['followUpActions', 'safeguardingActionsRequired']}
          >
            <Checkbox data-testid={`${testId}-safeguarding-checkbox`}>
              <Text>{t(incidentReportsConstants.SAFEGUARDING)}</Text>
            </Checkbox>
          </FormItem>
        )}
        <FormItem
          data-testid={`${testId}-notification-field`}
          valuePropName="checked"
          name={['followUpActions', 'notificationRequired']}
        >
          <Checkbox data-testid={`${testId}-notification-checkbox`}>
            <Text>{t(incidentReportsConstants.REGULATOR_NOTIFICATION)}</Text>
          </Checkbox>
        </FormItem>
        <FormItem
          data-testid={`${testId}-actions-field`}
          name={followUpActionsFieldName}
          rules={[
            {
              validator: (rule, value) => actionPlanValidator(value, t)
            }
          ]}
          full
        >
          <ActionsInput
            testId={`${testId}-actions-input`}
            options={actionInputOptions}
            defaultStatusValue={FollowUpActionStatus.notStarted}
            buttonText={t(incidentReportsConstants.ADD_ACTION)}
            showOwnerSelection={flagIncidentCaseActionAssignment}
          />
        </FormItem>
      </FormCard>
      <FormCard
        testId={`${testId}-debriefing`}
        title={t(incidentReportsConstants.DEBRIEFING)}
      >
        <FormItem
          data-testid={`${testId}-debriefing-notes-field`}
          label={<Text>{t(incidentReportsConstants.NOTES)}</Text>}
          name={['debriefingNotes']}
        >
          <TextArea
            data-testid={`${testId}-debriefing-notes-textarea`}
            autoSize={{
              minRows: 3
            }}
          />
        </FormItem>
      </FormCard>
      <FormCard
        testId={`${testId}-learning-outcomes`}
        title={t(incidentReportsConstants.LEARNING_OUTCOMES)}
      >
        <FormItem
          data-testid={`${testId}-root-cause-field`}
          label={<Text>{t(incidentReportsConstants.ROOT_CAUSE) + ':'}</Text>}
          name={['rootCause']}
          colon={false}
        >
          <TextArea
            data-testid={`${testId}-root-cause-textarea`}
            autoSize={{
              minRows: 3
            }}
          />
        </FormItem>
        <FormItem
          data-testid={`${testId}-preventative-actions-field`}
          label={<Text>{t(incidentReportsConstants.ACTIONS_TAKEN) + ':'}</Text>}
          name={['preventativeActions']}
          colon={false}
        >
          <TextArea
            data-testid={`${testId}-preventative-actions-textarea`}
            autoSize={{
              minRows: 3
            }}
          />
        </FormItem>
        <FormItem
          data-testid={`${testId}-other-notes-field`}
          label={<Text>{t(incidentReportsConstants.OTHER_NOTES)}</Text>}
          name={['otherNotes']}
        >
          <TextArea
            data-testid={`${testId}-other-notes-textarea`}
            autoSize={{
              minRows: 3
            }}
          />
        </FormItem>
      </FormCard>
    </Wrapper>
  )
}

const DamagesStatusRadioGroup = styled(Radio.Group)`
  background: transparent !important;

  .ant-radio-wrapper {
    background: transparent !important;
  }
`

const Wrapper = styled.div`
  padding-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Title = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default IncidentCaseFormFields
